/**
 * AI 助手快速操作按钮注册表(宿主页面侧,iframe 外部)。
 *
 * 与页面能力注册表(page-ability/registry.ts)同模式:
 *   - 挂在 window 单例上,供 liyu-pc-base(注册 API,`utils/function` 暴露)与
 *     liyu-pc-lib(AI 助手提供者)跨 bundle 共享,互不 import,只按约定读写单例
 *   - 按 label 去重:相同 label 的注册压入堆栈,查询取栈顶(最后注册且未注销)
 *   - 每次注册/注销都会派发变更事件,由 liyu-pc-lib 的提供者把当前最终生效的
 *     全部按钮配置一起推送到 iframe 内的 AI 助手(pi-web)
 */
export const QUICK_ACTION_REGISTRY_KEY = '__LY_AI_QUICK_ACTION__';
export const QUICK_ACTION_CHANGE_EVENT = 'ly-ai-quick-actions-changed';

export interface QuickActionButton {
  /** 按钮名称(去重 key,相同 label 压栈,注销后恢复上一个) */
  label: string;
  /** 点击后是否直接发送;false 表示作为输入框前缀,等待用户补充内容 */
  sendDirect: boolean;
  /** 按钮对应的提示词 */
  prompt: string;
}

interface QuickActionRegistryState {
  /** label -> 堆栈(后注册的在栈顶) */
  stack: Map<string, QuickActionButton[]>;
  register(button: QuickActionButton): () => void;
  list(): QuickActionButton[];
}

function notifyChange(): void {
  window.dispatchEvent(new CustomEvent(QUICK_ACTION_CHANGE_EVENT));
}

function ensureRegistry(): QuickActionRegistryState {
  const g = globalThis as Record<string, unknown>;
  const existing = g[QUICK_ACTION_REGISTRY_KEY] as QuickActionRegistryState | undefined;
  if (existing && typeof existing.register === 'function' && typeof existing.list === 'function') {
    return existing;
  }
  const state: QuickActionRegistryState = {
    stack: new Map(),
    register(button) {
      const normalized: QuickActionButton = {
        label: String(button.label ?? '').trim(),
        sendDirect: !!button.sendDirect,
        prompt: String(button.prompt ?? ''),
      };
      if (!normalized.label) {
        return () => {};
      }
      const list = state.stack.get(normalized.label) || [];
      list.push(normalized);
      state.stack.set(normalized.label, list);
      notifyChange();
      let alive = true;
      return () => {
        if (!alive) return;
        alive = false;
        const cur = state.stack.get(normalized.label);
        if (!cur) return;
        const idx = cur.indexOf(normalized);
        if (idx > -1) {
          cur.splice(idx, 1);
          if (cur.length === 0) {
            state.stack.delete(normalized.label);
          } else {
            state.stack.set(normalized.label, cur);
          }
          notifyChange();
        }
      };
    },
    list() {
      const result: QuickActionButton[] = [];
      // 按注册 key 的插入顺序取每个堆栈的栈顶(当前生效项)
      for (const list of state.stack.values()) {
        const top = list[list.length - 1];
        if (top) {
          result.push({ ...top });
        }
      }
      return result;
    },
  };
  g[QUICK_ACTION_REGISTRY_KEY] = state;
  return state;
}

/**
 * 注册一个 AI 助手快速操作按钮,返回解除注册的函数。
 *
 * - 相同 label 去重压栈:新注册覆盖展示,解除注册后恢复上一个同 label 的配置
 * - 注册/注销后会自动通知 liyu-pc-lib 提供者,把当前全部按钮推送到 iframe
 *
 * @example
 * const unregister = registerQuickAction({
 *   label: '分析报表',
 *   sendDirect: false,
 *   prompt: '请分析以下报表数据:',
 * });
 * // 组件卸载时
 * unregister();
 */
export function registerQuickAction(button: QuickActionButton): () => void {
  return ensureRegistry().register(button);
}

/** 列出当前最终生效的全部按钮配置(栈顶,按注册顺序) */
export function listQuickActions(): QuickActionButton[] {
  return ensureRegistry().list();
}

/**
 * 从菜单/路由 options 中提取快速操作按钮配置(`options.ai_quick_actions`)。
 * - 兼容字符串形式的 JSON(如后端原样存储字符串)
 * - 按 label 去重,重复时保留最后一条
 */
export function extractQuickActionsFromOptions(options: unknown): QuickActionButton[] {
  if (!options || typeof options !== 'object') return [];
  let rawList = (options as Record<string, unknown>).ai_quick_actions;
  if (typeof rawList === 'string') {
    try {
      rawList = JSON.parse(rawList);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(rawList)) return [];
  const seen = new Set<string>();
  const result: QuickActionButton[] = [];
  for (const raw of rawList) {
    if (!raw || typeof raw !== 'object') continue;
    const record = raw as Record<string, unknown>;
    const label = String(record.label ?? '').trim();
    if (!label || seen.has(label)) continue;
    seen.add(label);
    result.push({
      label,
      sendDirect: record.sendDirect === true,
      prompt: String(record.prompt ?? ''),
    });
  }
  return result;
}
